import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import {
  BulkDeleteWithConfirmButton,
  Create,
  Datagrid,
  DeleteWithConfirmButton,
  Edit,
  List,
  Pagination,
  SaveButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useListContext,
  TopToolbar,
  FunctionField,
  useNotify,
  useRedirect,
  SelectInput,
} from "react-admin";
import "./styles.css";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField as MuiTextField,
} from "@mui/material";
import NoDataEmpty from "../NoDataEmpty";
import EditIcon from "@mui/icons-material/Edit";
import Add from "@mui/icons-material/Add";
import SyncIcon from "@mui/icons-material/Sync";
import { API_BASE } from "../../providers/env";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';

type Params = { agentId?: string };

const SearchBar = ({
  defaultKeyword,
  onSearch,
}: {
  defaultKeyword: string;
  onSearch: (kw: string) => void;
}) => {
  const [keyword, setKeyword] = useState(defaultKeyword);

  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <MuiTextField
        size="small"
        placeholder="Search keyword"
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
      />
      <Button variant="contained" onClick={() => onSearch(keyword)}>
        Search
      </Button>
    </Box>
  );
};

const ListActions: React.FC<{ agentId?: string }> = ({ agentId }) => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);

  const handleSync = async () => {
    if (!agentId) {
      notify("Agent ID is missing", { type: "error" });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/environ-variables/sync-to-apis?agent_id=${agentId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const data = await response.json();
      if (response.ok) {
        notify(data.detail, { type: "success" });
      } else {
        notify(data.detail || "Sync failed", { type: "error" });
      }
    } catch (err) {
      notify("An error occurred during sync", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <TopToolbar>
      <PermissionGuard resource="environ_variables" action="create">
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleSync}
            disabled={loading}
            startIcon={<SyncIcon />}
          >
            {loading ? "Syncing..." : "Sync to APIs"}
          </Button>
          <Button
            variant="contained"
            onClick={() =>
              navigate(`/environ-variables/${agentId ?? ""}/create`)
            }
          >
            <Add fontSize="small" sx={{ mr: 0.5 }} /> Create
          </Button>
        </Box>
      </PermissionGuard>
    </TopToolbar>
  );
};

const ApiVariablesListContent = ({ agentId }: { agentId?: string }) => {
  const { filterValues = {}, setFilters } = useListContext();
  const { hasPermission } = useCustomPermissions();

  const handleSearch = (kw: string) => {
    if (typeof setFilters === "function") {
      setFilters({ ...filterValues, keyword: kw }, null);
    }
  };

  return (
    <Card sx={{ m: 2 }}>
      <CardHeader title="Manage Variables" />
      <CardContent>
        <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between" }}>
          <SearchBar
            defaultKeyword={filterValues.keyword || ""}
            onSearch={handleSearch}
          />
        </Box>

        <Datagrid
          rowClick={hasPermission('environ_variables', 'edit') ? (id: any) => `/environ-variables/${agentId}/${id}` : false}
          bulkActionButtons={
            hasPermission('environ_variables', 'delete') ? (
              <BulkDeleteWithConfirmButton
                confirmTitle="Are you sure?"
                confirmContent="This will permanently delete the selected variables."
                mutationMode="pessimistic"
              />
            ) : false
          }
        >
          <TextField source="key" label="Key" sx={{ minWidth: 150 }} />
          <TextField source="value" label="Value" sx={{ minWidth: 200 }} />
          <FunctionField
            source="type"
            label="Sync Type"
            sx={{ minWidth: 150, whiteSpace: "nowrap" }}
            render={(record: any) => {
              const types: any = {
                basic_auth: "Basic Auth",
                bearer_token: "Bearer Token",
                header: "Header",
                body: "Body",
                param: "Param",
              };
              return types[record.type] || record.type || "-";
            }}
          />

          {(hasPermission('environ_variables', 'edit') || hasPermission('environ_variables', 'delete')) && (
            <FunctionField
              label="Actions"
              render={(record: any) => <ActionsFunctionField agentId={agentId} />}
              cellClassName="column-actions"
            />
          )}
        </Datagrid>
      </CardContent>
    </Card>
  );
};

const ActionsFunctionField = ({ agentId }: { agentId?: string }) => {
  const { hasPermission } = useCustomPermissions();

  return (
    <FunctionField
      label="Actions"
      render={(record: any) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <PermissionGuard resource="environ_variables" action="edit">
            <Button
              component={RouterLink}
              to={`/environ-variables/${agentId}/${record.id}`}
              variant="outlined"
              size="small"
            >
              <EditIcon style={{ fontSize: 16, marginRight: 8 }} /> Edit
            </Button>
          </PermissionGuard>

          <PermissionGuard resource="environ_variables" action="delete">
            <DeleteWithConfirmButton
              record={record}
              confirmTitle="Are you sure?"
              confirmContent="You won't be able to revert this!"
              mutationMode="pessimistic"
              redirect={`/environ-variables/${agentId}`}
            />
          </PermissionGuard>
        </Box>
      )}
    />
  );
};

export const ApiVariablesList = (props: any) => {
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <List
      {...props}
      perPage={25}
      pagination={<Pagination rowsPerPageOptions={[25, 50, 100]} />}
      actions={<ListActions agentId={agentId} />}
      empty={<NoDataEmpty entityKey="environ-variables" agentId={agentId} />}
    >
      <ApiVariablesListContent agentId={agentId} />
    </List>
  );
};

// Edit
const CustomToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);
const transformEdit = (data: any) => ({
  ...data,
  key: data.key?.trim(),
  value: data.value?.trim(),
});

export const ApiVariablesEdit = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <Edit
      {...props}
      title={false}
      transform={(data: any) => ({ ...transformEdit(data), agent_id: agentId })}
      component="div"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Updated successfully", { type: "success" });
          redirect(`/environ-variables/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ margin: 2 }}>
        <CardHeader title="Variable Update" />
        <CardContent>
          <SimpleForm toolbar={<CustomToolbar />}>
            <TextInput source="key" label="Key" fullWidth />
            <TextInput
              source="value"
              label="Value"
              fullWidth
              minRows={4}
              multiline
            />
            <SelectInput
              source="type"
              label="Sync Type"
              choices={[
                { id: "basic_auth", name: "Basic Auth" },
                { id: "bearer_token", name: "Bearer Token" },
                { id: "header", name: "Header" },
                { id: "body", name: "Body" },
                { id: "param", name: "Param" },
              ]}
              fullWidth
            />
          </SimpleForm>
        </CardContent>
      </Card>
    </Edit>
  );
};

export const ApiVariablesCreate = (props: any) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();

  return (
    <Create
      {...props}
      transform={(data: any) => ({ ...transformEdit(data), agent_id: agentId })}
      title={false}
      component="div"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Created successfully", { type: "success" });
          redirect(`/environ-variables/${agentId || ""}`);
        },
        onError: (error: any) =>
          notify(`Error: ${error?.message}`, { type: "error" }),
      }}
    >
      <Card sx={{ margin: 2 }}>
        <CardHeader title="Create Variable" />
        <CardContent>
          <SimpleForm>
            <TextInput source="key" label="Key" fullWidth />
            <TextInput
              source="value"
              label="Value"
              fullWidth
              minRows={4}
              multiline
            />
            <SelectInput
              source="type"
              label="Sync Type"
              choices={[
                { id: "basic_auth", name: "Basic Auth" },
                { id: "bearer_token", name: "Bearer Token" },
                { id: "header", name: "Header" },
                { id: "body", name: "Body" },
                { id: "param", name: "Param" },
              ]}
              fullWidth
            />
          </SimpleForm>
        </CardContent>
      </Card>
    </Create>
  );
};
